// src/pages/Community/StoryCharactersIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { db } from "../../firebase";
import "./css/StoryCharactersIndex.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import CharacterForm from "../Profile/CharacterStoryForm";

export default function StoryCharactersIndex({ user: userProp }) {
  const [characters, setCharacters] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState(userProp ?? null);

  useEffect(() => {
    if (userProp !== undefined) setMe(userProp ?? null);
  }, [userProp]);

  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setMe((prev) => (userProp !== undefined ? (userProp ?? null) : u));
      setAuthReady(true);
    });
    return off;
  }, [userProp]);

  const [selectedKey, setSelectedKey] = useState("all");
  const [showModal, setShowModal] = useState(false);

  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const isMine = (c, user) => {
    const uid = user?.uid;
    return !!uid && [c.authorUid, c.createdBy, c.userId, c.ownerId].some((x) => x === uid);
  };

  useEffect(() => {
    const q = query(collection(db, "characters"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() || 0) -
              (a.createdAt?.toMillis?.() || 0)
          );
        setCharacters(docs);
        setLoading(false);
      },
      (err) => {
        console.error("characters query error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const list = useMemo(() => {
    let arr = [...characters];

    if (selectedKey === "mine") {
      arr = arr.filter((c) => isMine(c, me));
    } else if (selectedKey !== "all") {
      const f = norm(selectedKey);
      arr = arr.filter((c) => norm(c.gameKey || c.game) === f);
    }

    if (search.trim()) {
      const q = norm(search);
      arr = arr.filter((c) =>
        [
          c.characterName,
          c.authorName,
          c.game,
          c.gameKey,
          c.synopsis,
          c.story,
          ...(c.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    arr.sort((a, b) => {
      const an = norm(a.characterName);
      const bn = norm(b.characterName);
      if (an && bn && an !== bn) return an.localeCompare(bn);
      return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    });

    return arr;
  }, [characters, search, selectedKey, me]);

  const labelForGameKey = (gk) => {
    if (!gk) return "Unknown";
    const up = gk.toUpperCase().replace("-", " ");
    const key = gk.toLowerCase();
    if (key === "gta-6") return "GTA VI";
    if (key === "gta-5") return "GTA V";
    if (key === "gta-4" || key === "gta-iv") return "GTA IV";
    return up;
  };

  const authInitializing = !authReady && me == null;

  return (
    <>
      <div id="sc" className="char-wrap">
        <ToolbarHeader
          children={
            <div className="forum-tabs">
              {GTA_GAME_TABS.concat(me ? [{ key: "mine", label: "My Characters" }] : []).map(
                ({ key, label }) => {
                  const active = selectedKey === key;
                  return (
                    <span
                      key={key}
                      className={`tab-text ${active ? "active" : ""}`}
                      onClick={() => setSelectedKey(key)}
                      role="button"
                      aria-current={active ? "page" : undefined}
                    >
                      {label}
                    </span>
                  );
                }
              )}
            </div>
          }
          search={search}
          setSearch={setSearch}
          placeholder="Search characters, games, tags…"
          onAddMission={() => (me ? setShowModal(true) : alert("Please sign in to add a character."))}
          user={me}
          addLabel="＋ Add Character"
          addDisabled={!me}
        />

        <ul className="char-layout">
          {(loading || authInitializing) &&
            Array.from({ length: 6 }).map((_, i) => (
              <li key={`sk-${i}`} className="char-card char-card--skeleton">
                <div className="char-thumb skeleton" />
                <div className="char-card-body">
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line short" />
                </div>
              </li>
            ))}

          {!loading &&
            !authInitializing &&
            list.map((c) => (
              <li key={c.id} className="char-card">
                <Link
                  to={`/community/story-characters/${c.id}`}
                  className="char-card-link"
                >
                  {/* Thumbnail + overlay now contains the author/role/snippet */}
                  <div className="char-thumb">
                    {c.imageUrl ? (
                      <img
                        className="char-img"
                        src={c.imageUrl}
                        alt={c.characterName || "Character"}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="char-img char-img--placeholder">🃏</div>
                    )}

                    <div className="char-toprow">
                      <span className="char-badge">
                        {(GTA_GAME_TABS.find((g) => g.key === norm(c.gameKey || c.game))?.label) ||
                          labelForGameKey(norm(c.gameKey || c.game))}
                      </span>

                      {isMine(c, me) && (
                        <span
                          className="char-chip char-chip--owner"
                          title="You created this"
                        >
                          👑
                        </span>
                      )}
                    </div>

                    <div className="char-titlebar">
                     
                    </div>

                    {/* NEW: overlay with author / role / snippet on top of image */}
                    <div className="char-overlay" aria-hidden={false}>
                      <div className="char-overlay-line char-overlay-author">
                        <h6 className="overlay-name">{c.characterName || "Unnamed"}</h6>
                        {c.authorName && (
                          <span className="overlay-author">by {c.authorName}</span>
                        )}
                      </div>

                      {(c.synopsis || c.story) && (
                        <div className="char-overlay-line char-overlay-synopsis">
                          {((c.synopsis && c.synopsis.trim()) || (c.story && c.story.trim()) || "")
                            .slice(0, 140)
                            .replace(/\s+\S*$/, (m) => m) /* avoid mid-word cut as best-effort */}
                          {((c.synopsis || c.story) || "").length > 140 && "…"}
                        </div>
                      )}

                    </div>
                  </div>           
                </Link>
              </li>
            ))}

          {!loading && !authInitializing && list.length === 0 && (
            <div className="char-empty">No characters match your search.</div>
          )}
        </ul>
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Add a Character"
        size="md"
        closeOnBackdrop={true}
        showClose={true}
      >
        {me ? (
          <CharacterForm
            user={me}
            enforceAdmin={false}
            isAdmin={false}
            onSaved={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
            initial={{
              gameKey: selectedKey === "all" || selectedKey === "mine" ? "gta-6" : selectedKey,
              game: GTA_GAME_TABS.find((g) => g.key === selectedKey)?.label || "GTA VI",
              tags: [],
            }}
          />
        ) : (
          <div style={{ padding: 16 }}>Please log in to continue.</div>
        )}
      </Modal>
    </>
  );
}
